import { Injectable } from "@angular/core";
import { HALEON_APP_HEADER } from "../constants";
import { map, switchMap } from "rxjs/operators";
import {
  ImageResponse,
  ImageService,
  Patient,
  PatientService,
  SessionIndexed,
  Treatment,
  TreatmentService,
} from "@kells/clinic-one/apis";
import {
  ReviewJob,
  ReviewJobService,
} from "@kells/clinic-one/data-access/review-jobs";
import { combineLatest, Observable } from "rxjs";
import { UserService } from "@kells/clinic-one/data-access/users";
import { FindingPredictionService } from "@kells/apis/ml";

@Injectable({
  providedIn: "root",
})
export class HaleonDataAccessService {
  constructor(
    private readonly patientService: PatientService,
    private readonly reviewJobService: ReviewJobService,
    private readonly imageService: ImageService,
    private readonly treatmentService: TreatmentService,
    private readonly userService: UserService,
    private readonly findingPredictionService: FindingPredictionService
  ) { }

  private getHaleonHeaders() {
    return {
      [HALEON_APP_HEADER]: "true",
    };
  }

  getPatientLatestSessionWithImages(
    patient: Patient
  ): Observable<{
    patient: Patient;
    session: ReviewJob | null;
    images: SessionIndexed<ImageResponse>;
    treatments: Treatment[];
  }> {
    return combineLatest([
      this.reviewJobService
        .getPatientReviewJobs(patient.id, this.getHaleonHeaders())
        .pipe(
          map(
            (reviewJobs) =>
              [...reviewJobs].sort(
                (a, b) =>
                  new Date(b.sessionCreatedAt).getTime() -
                  new Date(a.sessionCreatedAt).getTime()
              )[0]
          )
        ),
      this.patientService.getSessionDateIndexedImages(
        patient.id,
        this.getHaleonHeaders()
      ),
      this.treatmentService.getPatientTreatments(
        patient.id,
        this.getHaleonHeaders()
      ),
    ]).pipe(
      map(([session, images, treatments]) => ({
        patient,
        session,
        images,
        treatments,
      }))
    );
  }

  getPatient(email: string) {
    return this.patientService.getPatientByEmail(
      email,
      this.getHaleonHeaders()
    );
  }

  getUser(email: string) {
    return this.userService.getUserByEmail(email, this.getHaleonHeaders());
  }

  getPatientReport(email: string) {
    return this.getPatient(email).pipe(
      switchMap((patient) => this.getPatientLatestSessionWithImages(patient))
    );
  }

  getPhotoPredictions(imageId: string) {
    return this.findingPredictionService.getPhotoPredictions(
      imageId,
      this.getHaleonHeaders()
    );
  }

  createSession(patient: Patient) {
    return this.reviewJobService.createSession(
      patient.id,
      true,
      this.getHaleonHeaders()
    );
  }

  uploadImageForPatient(patient: Patient, image: File, session_id: string) {
    return this.imageService.uploadImageForPatient(
      patient.id,
      image,
      true,
      session_id,
      this.getHaleonHeaders()
    );
  }

  markSessionReady(session_id: string) {
    return this.imageService.markSessionReady(
      session_id,
      this.getHaleonHeaders()
    );
  }

  getTreatments(patient: Patient) {
    return this.treatmentService.getPatientTreatments(
      patient.id,
      this.getHaleonHeaders()
    );
  }
}
